import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2, Save, RefreshCw, Eye, Wrench, X, Info } from 'lucide-react';
import { fieldPartnersApi } from '@/api/field-partners';
import {
  useFieldProgramConfig,
  type FieldProgramConfig,
  type PrizeBand,
  type PhotoRequirement,
  type TierLevel,
  type UpsellEntry,
  type PeriodicRewardConfig,
} from '@/hooks/useFieldProgramConfig';

const PARTNER_TYPES = [
  'PLUMBER',
  'ELECTRICIAN',
  'TECHNICIAN',
  'INSTALLER',
  'MECHANIC',
  'CARPENTER',
  'CUSTOM',
];
const KYC_VENDORS = ['hyperverge', 'digio', 'none'];
const LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'bgc', label: 'Haryanvi' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'ur', label: 'Urdu' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'mr', label: 'Marathi' },
  { code: 'bn', label: 'Bengali' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
];

const REWARD_TYPES = ['GIFT_HAMPER', 'TRIP', 'CASH', 'VOUCHER'] as const;

const DEFAULT_CONFIG: FieldProgramConfig = {
  appName: 'Field Partners',
  partnerType: 'PLUMBER',
  partnerTypeLabel: 'Field Partner',
  brandName: 'Field Partner Program',
  logoUrl: '',
  primaryColor: '#2563eb',
  tagline: '',
  defaultLanguages: ['en', 'hi'],
  defaultLanguage: 'en',
  kycRequired: true,
  kycVendor: 'hyperverge',
  demoMode: false,
  demoModeNotice: '',
  prizeStructure: [
    { amount: 10, weight: 60 },
    { amount: 50, weight: 30 },
    { amount: 500, weight: 10 },
  ],
  photoRequirements: [
    { key: 'tank_label', label: 'Tank label / serial', required: true },
    { key: 'installation_wide', label: 'Installation wide shot', required: true },
    { key: 'customer_area', label: 'Customer area', required: true },
    { key: 'partner_selfie', label: 'Partner selfie', required: true },
  ],
  tierStructure: [
    { name: 'BRONZE', threshold: 0, perUnit: 100, multiplier: 1 },
    { name: 'SILVER', threshold: 25, perUnit: 150, multiplier: 1.25 },
    { name: 'GOLD', threshold: 75, perUnit: 200, multiplier: 1.5 },
  ],
  upsellCatalog: [],
  quarterlyRewardConfig: null,
  halfYearlyRewardConfig: null,
  enabled: false,
};

export function FieldProgramConfigPage() {
  const { config: serverConfig, loading, reload, enabled } = useFieldProgramConfig();
  const [draft, setDraft] = useState<FieldProgramConfig>(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (serverConfig) {
      setDraft({
        ...DEFAULT_CONFIG,
        ...serverConfig,
        prizeStructure: serverConfig.prizeStructure ?? [],
        photoRequirements: serverConfig.photoRequirements ?? [],
        tierStructure: serverConfig.tierStructure ?? [],
        upsellCatalog: serverConfig.upsellCatalog ?? [],
        defaultLanguages: serverConfig.defaultLanguages ?? ['en'],
      });
    }
  }, [serverConfig]);

  const set = <K extends keyof FieldProgramConfig>(key: K, val: FieldProgramConfig[K]) =>
    setDraft((d) => ({ ...d, [key]: val }));

  const totalPrizeWeight = useMemo(
    () => draft.prizeStructure.reduce((s, b) => s + (Number(b.weight) || 0), 0),
    [draft.prizeStructure],
  );
  const weightsValid = Math.abs(totalPrizeWeight - 100) < 0.01;

  const handleSave = async () => {
    if (!draft.appName.trim() || !draft.partnerTypeLabel.trim() || !draft.brandName.trim()) {
      alert('App Name, Partner Type Label, and Brand Name are required');
      return;
    }
    if (draft.prizeStructure.length > 0 && !weightsValid) {
      if (!confirm(`Prize weights sum to ${totalPrizeWeight}, not 100. Save anyway?`)) return;
    }
    setSaving(true);
    try {
      await fieldPartnersApi.updateFieldProgramConfig(draft);
      await reload();
      alert('Saved');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reload from server and discard unsaved changes?')) return;
    await reload();
  };

  if (loading && !serverConfig) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--text-primary)]">
            <Wrench size={22} className="text-[var(--saffron-600)]" />
            Program Config
          </h1>
          <p className="text-sm text-[var(--text-muted)]">
            Rebrand the Field Partners module for your tenant. Changes are immediate across web + mobile.
            {enabled === false && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                <Info size={11} /> Module disabled — toggle “Enabled” at the bottom to activate
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPreview(true)}
            className="flex items-center gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
          >
            <Eye size={14} /> Preview
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
          >
            <RefreshCw size={14} /> Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-60"
          >
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Branding */}
      <Section title="Branding" hint="App name, partner type, brand identity. Drives every label across the module.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="App Name"
            tooltip="Top-level brand for the program (e.g., 'Stellar Plumber'). Shown in sidebar and mobile splash."
          >
            <input
              value={draft.appName}
              onChange={(e) => set('appName', e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Partner Type" tooltip="Internal partner classification (used for analytics + integration mapping).">
            <select
              value={draft.partnerType}
              onChange={(e) => set('partnerType', e.target.value)}
              className="input"
            >
              {PARTNER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Partner Type Label (singular)"
            tooltip="What you call a single partner in the UI (e.g., 'Plumber', 'Electrician'). Used in every page title."
          >
            <input
              value={draft.partnerTypeLabel}
              onChange={(e) => set('partnerTypeLabel', e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Brand Name" tooltip="Full marketing brand (e.g., 'Sun Stellar Plumber Partners'). Shown on app onboarding.">
            <input
              value={draft.brandName}
              onChange={(e) => set('brandName', e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Logo URL">
            <input
              value={draft.logoUrl || ''}
              onChange={(e) => set('logoUrl', e.target.value)}
              placeholder="https://..."
              className="input"
            />
          </Field>
          <Field label="Primary Color">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={draft.primaryColor}
                onChange={(e) => set('primaryColor', e.target.value)}
                className="h-9 w-14 cursor-pointer rounded border border-[var(--border-primary)]"
              />
              <input
                value={draft.primaryColor}
                onChange={(e) => set('primaryColor', e.target.value)}
                className="input flex-1 font-mono text-xs"
              />
            </div>
          </Field>
          <Field label="Tagline" tooltip="Short marketing line shown on splash and onboarding.">
            <input
              value={draft.tagline || ''}
              onChange={(e) => set('tagline', e.target.value)}
              placeholder="e.g., Earn more on every install."
              className="input"
            />
          </Field>
        </div>
      </Section>

      {/* Languages */}
      <Section title="Languages" hint="Pick the languages you want to ship in the partner mobile app.">
        <Field label="Available Languages">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {LANGUAGES.map((l) => {
              const checked = draft.defaultLanguages.includes(l.code);
              return (
                <label
                  key={l.code}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...draft.defaultLanguages, l.code]
                        : draft.defaultLanguages.filter((c) => c !== l.code);
                      set('defaultLanguages', next);
                      if (!next.includes(draft.defaultLanguage) && next.length > 0) {
                        set('defaultLanguage', next[0]);
                      }
                    }}
                  />
                  <span>
                    {l.label}{' '}
                    <span className="text-[10px] text-[var(--text-muted)]">({l.code})</span>
                  </span>
                </label>
              );
            })}
          </div>
        </Field>
        <Field label="Default Language" tooltip="Initial language new partners see when they install the app.">
          <select
            value={draft.defaultLanguage}
            onChange={(e) => set('defaultLanguage', e.target.value)}
            className="input max-w-xs"
          >
            {draft.defaultLanguages.map((c) => {
              const lang = LANGUAGES.find((l) => l.code === c);
              return (
                <option key={c} value={c}>
                  {lang?.label || c}
                </option>
              );
            })}
          </select>
        </Field>
      </Section>

      {/* KYC */}
      <Section title="KYC Settings" hint="Enable, choose vendor, and toggle demo mode.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Toggle
            label="KYC Required"
            checked={draft.kycRequired}
            onChange={(v) => set('kycRequired', v)}
            hint="When off, partners can take installation jobs without ID verification (NOT recommended for production)."
          />
          <Field label="KYC Vendor">
            <select
              value={draft.kycVendor}
              onChange={(e) => set('kycVendor', e.target.value)}
              disabled={!draft.kycRequired}
              className="input disabled:opacity-50"
            >
              {KYC_VENDORS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Toggle
            label="Demo Mode"
            checked={draft.demoMode}
            onChange={(v) => set('demoMode', v)}
            hint="Show a banner in the app marking it as demo. KYC + payouts are simulated."
          />
          <Field label="Demo Mode Notice">
            <textarea
              value={draft.demoModeNotice || ''}
              onChange={(e) => set('demoModeNotice', e.target.value)}
              disabled={!draft.demoMode}
              rows={2}
              className="input disabled:opacity-50"
              placeholder="This is a demo build. No real money is paid out."
            />
          </Field>
        </div>
      </Section>

      {/* Prize Structure */}
      <Section
        title="Prize Structure"
        hint={`Cash reward sticker prize bands. Weights must sum to 100. Currently: ${totalPrizeWeight.toFixed(0)}`}
      >
        <RowEditor<PrizeBand>
          rows={draft.prizeStructure}
          onChange={(rows) => set('prizeStructure', rows)}
          empty={{ amount: 0, weight: 0 }}
          columns={[
            {
              key: 'amount',
              label: 'Amount (₹)',
              render: (row, i, update) => (
                <input
                  type="number"
                  min={0}
                  value={row.amount}
                  onChange={(e) => update(i, { ...row, amount: Number(e.target.value) })}
                  className="input"
                />
              ),
            },
            {
              key: 'weight',
              label: 'Weight (%)',
              render: (row, i, update) => (
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={row.weight}
                  onChange={(e) => update(i, { ...row, weight: Number(e.target.value) })}
                  className="input"
                />
              ),
            },
          ]}
        />
        {!weightsValid && draft.prizeStructure.length > 0 && (
          <p className="mt-2 text-xs text-amber-600">
            ⚠ Weights total {totalPrizeWeight} — must equal 100 for reward distribution to be valid.
          </p>
        )}
      </Section>

      {/* Photo Requirements */}
      <Section title="Photo Requirements" hint="What partners must capture during an install.">
        <RowEditor<PhotoRequirement>
          rows={draft.photoRequirements}
          onChange={(rows) => set('photoRequirements', rows)}
          empty={{ key: '', label: '', required: true }}
          columns={[
            {
              key: 'key',
              label: 'Key',
              render: (row, i, update) => (
                <input
                  value={row.key}
                  onChange={(e) => update(i, { ...row, key: e.target.value })}
                  placeholder="tank_label"
                  className="input font-mono text-xs"
                />
              ),
            },
            {
              key: 'label',
              label: 'Label',
              render: (row, i, update) => (
                <input
                  value={row.label}
                  onChange={(e) => update(i, { ...row, label: e.target.value })}
                  placeholder="Tank label / serial"
                  className="input"
                />
              ),
            },
            {
              key: 'required',
              label: 'Required',
              render: (row, i, update) => (
                <input
                  type="checkbox"
                  checked={row.required}
                  onChange={(e) => update(i, { ...row, required: e.target.checked })}
                />
              ),
            },
          ]}
        />
      </Section>

      {/* Tier Structure */}
      <Section title="Tier Structure" hint="Min installs to reach each tier and the per-unit / multiplier reward.">
        <RowEditor<TierLevel>
          rows={draft.tierStructure}
          onChange={(rows) => set('tierStructure', rows)}
          empty={{ name: '', threshold: 0, perUnit: 0, multiplier: 1 }}
          columns={[
            {
              key: 'name',
              label: 'Tier Name',
              render: (row, i, update) => (
                <input
                  value={row.name}
                  onChange={(e) => update(i, { ...row, name: e.target.value })}
                  placeholder="GOLD"
                  className="input"
                />
              ),
            },
            {
              key: 'threshold',
              label: 'Min Installs',
              render: (row, i, update) => (
                <input
                  type="number"
                  value={row.threshold}
                  onChange={(e) => update(i, { ...row, threshold: Number(e.target.value) })}
                  className="input"
                />
              ),
            },
            {
              key: 'perUnit',
              label: 'Per Unit (₹)',
              render: (row, i, update) => (
                <input
                  type="number"
                  value={row.perUnit}
                  onChange={(e) => update(i, { ...row, perUnit: Number(e.target.value) })}
                  className="input"
                />
              ),
            },
            {
              key: 'multiplier',
              label: 'Multiplier',
              render: (row, i, update) => (
                <input
                  type="number"
                  step="0.05"
                  value={row.multiplier}
                  onChange={(e) => update(i, { ...row, multiplier: Number(e.target.value) })}
                  className="input"
                />
              ),
            },
          ]}
        />
      </Section>

      {/* Upsell Catalog */}
      <Section title="Upsell Catalog" hint="Optional add-ons partners can pitch — bonus paid on success.">
        <RowEditor<UpsellEntry>
          rows={draft.upsellCatalog}
          onChange={(rows) => set('upsellCatalog', rows)}
          empty={{ key: '', label: '', bonus: 0 }}
          columns={[
            {
              key: 'key',
              label: 'Key',
              render: (row, i, update) => (
                <input
                  value={row.key}
                  onChange={(e) => update(i, { ...row, key: e.target.value })}
                  placeholder="water_purifier"
                  className="input font-mono text-xs"
                />
              ),
            },
            {
              key: 'label',
              label: 'Label',
              render: (row, i, update) => (
                <input
                  value={row.label}
                  onChange={(e) => update(i, { ...row, label: e.target.value })}
                  placeholder="Water Purifier"
                  className="input"
                />
              ),
            },
            {
              key: 'bonus',
              label: 'Bonus (₹)',
              render: (row, i, update) => (
                <input
                  type="number"
                  value={row.bonus}
                  onChange={(e) => update(i, { ...row, bonus: Number(e.target.value) })}
                  className="input"
                />
              ),
            },
          ]}
        />
      </Section>

      {/* Rewards */}
      <Section title="Periodic Rewards" hint="Quarterly + half-yearly milestone rewards (gift hamper, trip, cash, voucher).">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <RewardEditor
            title="Quarterly Reward"
            value={draft.quarterlyRewardConfig ?? null}
            onChange={(v) => set('quarterlyRewardConfig', v)}
          />
          <RewardEditor
            title="Half-Yearly Reward"
            value={draft.halfYearlyRewardConfig ?? null}
            onChange={(v) => set('halfYearlyRewardConfig', v)}
          />
        </div>
      </Section>

      {/* Status */}
      <Section title="Status" hint="Master switch — turn the entire Field Partners module on or off.">
        <Toggle
          label="Enabled"
          checked={draft.enabled}
          onChange={(v) => set('enabled', v)}
          hint="When OFF, all Field Partners pages show a 'not enabled' state and the sidebar section collapses."
        />
      </Section>

      <div className="sticky bottom-0 -mx-6 flex justify-end gap-2 border-t border-[var(--border-primary)] bg-[var(--bg-surface)]/90 px-6 py-3 backdrop-blur">
        <button
          onClick={handleReset}
          className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)]"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          <Save size={14} /> {saving ? 'Saving...' : 'Save Config'}
        </button>
      </div>

      {showPreview && <PreviewModal config={draft} onClose={() => setShowPreview(false)} />}

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.5rem;
          border: 1px solid var(--border-primary);
          background: var(--bg-inset);
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: var(--text-primary);
        }
        .input:focus { outline: none; border-color: rgb(59 130 246); }
      `}</style>
    </div>
  );
}

/* ----------------------- Sub-components ----------------------- */

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-[var(--text-primary)]">{title}</h2>
        {hint && <p className="mt-0.5 text-xs text-[var(--text-muted)]">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1 text-xs font-medium text-[var(--text-secondary)]">
        {label}
        {tooltip && (
          <span title={tooltip} className="cursor-help text-[var(--text-muted)]">
            <Info size={11} />
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <div>
      <label className="flex cursor-pointer items-center gap-2">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="text-sm font-medium text-[var(--text-primary)]">{label}</span>
      </label>
      {hint && <p className="ml-6 mt-0.5 text-xs text-[var(--text-muted)]">{hint}</p>}
    </div>
  );
}

interface ColumnDef<T> {
  key: string;
  label: string;
  render: (row: T, index: number, update: (i: number, next: T) => void) => React.ReactNode;
}

function RowEditor<T>({
  rows,
  onChange,
  empty,
  columns,
}: {
  rows: T[];
  onChange: (next: T[]) => void;
  empty: T;
  columns: ColumnDef<T>[];
}) {
  const update = (i: number, next: T) => {
    const copy = [...rows];
    copy[i] = next;
    onChange(copy);
  };
  const remove = (i: number) => {
    const copy = [...rows];
    copy.splice(i, 1);
    onChange(copy);
  };
  const add = () => onChange([...rows, { ...empty }]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-primary)] text-left text-[10px] font-semibold uppercase text-[var(--text-muted)]">
            {columns.map((c) => (
              <th key={c.key} className="py-2 pr-3">
                {c.label}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-primary)]">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="py-4 text-center text-xs text-[var(--text-muted)]">
                No rows — click “Add Row”.
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} className="py-2 pr-3 align-top">
                    {c.render(row, i, update)}
                  </td>
                ))}
                <td className="py-2 align-top">
                  <button
                    onClick={() => remove(i)}
                    className="rounded p-1.5 text-red-600 hover:bg-red-50"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <button
        onClick={add}
        className="mt-2 flex items-center gap-1 rounded-lg border border-dashed border-[var(--border-primary)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
      >
        <Plus size={12} /> Add Row
      </button>
    </div>
  );
}

function RewardEditor({
  title,
  value,
  onChange,
}: {
  title: string;
  value: PeriodicRewardConfig | null;
  onChange: (v: PeriodicRewardConfig | null) => void;
}) {
  const enabled = value != null;
  const v = value ?? { targetCount: 0, rewardType: 'GIFT_HAMPER', rewardName: '', rewardValue: 0 };
  return (
    <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
        <label className="flex cursor-pointer items-center gap-1 text-xs text-[var(--text-secondary)]">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? { targetCount: 0, rewardType: 'GIFT_HAMPER', rewardName: '', rewardValue: 0 }
                  : null,
              )
            }
          />
          Enabled
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Target count">
          <input
            type="number"
            disabled={!enabled}
            value={v.targetCount}
            onChange={(e) => onChange({ ...v, targetCount: Number(e.target.value) })}
            className="input disabled:opacity-50"
          />
        </Field>
        <Field label="Reward Type">
          <select
            disabled={!enabled}
            value={v.rewardType}
            onChange={(e) => onChange({ ...v, rewardType: e.target.value as any })}
            className="input disabled:opacity-50"
          >
            {REWARD_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reward Name">
          <input
            disabled={!enabled}
            value={v.rewardName}
            onChange={(e) => onChange({ ...v, rewardName: e.target.value })}
            placeholder="Diwali Hamper"
            className="input disabled:opacity-50"
          />
        </Field>
        <Field label="Reward Value (₹)">
          <input
            type="number"
            disabled={!enabled}
            value={v.rewardValue}
            onChange={(e) => onChange({ ...v, rewardValue: Number(e.target.value) })}
            className="input disabled:opacity-50"
          />
        </Field>
      </div>
    </div>
  );
}

function PreviewModal({
  config,
  onClose,
}: {
  config: FieldProgramConfig;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">Mobile Preview</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-[var(--bg-surface-hover)]">
            <X size={16} />
          </button>
        </div>
        <div className="bg-[var(--bg-inset)] p-6">
          <div
            className="mx-auto flex h-[480px] w-[260px] flex-col items-center justify-center rounded-3xl border-4 border-gray-800 bg-white p-6 shadow-2xl"
            style={{ background: `linear-gradient(135deg, ${config.primaryColor}22, white)` }}
          >
            {config.logoUrl ? (
              <img src={config.logoUrl} alt="logo" className="mb-3 h-16 w-16 rounded-xl object-contain" />
            ) : (
              <div
                className="mb-3 flex h-16 w-16 items-center justify-center rounded-xl text-2xl font-bold text-white"
                style={{ background: config.primaryColor }}
              >
                {(config.appName?.[0] || 'F').toUpperCase()}
              </div>
            )}
            <h3 className="text-center text-lg font-bold text-gray-900">{config.appName}</h3>
            <p className="mt-1 text-center text-xs text-gray-600">{config.brandName}</p>
            {config.tagline && (
              <p className="mt-3 text-center text-[11px] italic text-gray-500">"{config.tagline}"</p>
            )}
            <div className="mt-6 w-full space-y-2 text-[11px] text-gray-700">
              <div>Login as: {config.partnerTypeLabel}</div>
              <div>Languages: {config.defaultLanguages.join(', ')}</div>
              <div>KYC: {config.kycRequired ? config.kycVendor : 'disabled'}</div>
              {config.demoMode && (
                <div className="rounded bg-amber-100 px-2 py-1 text-[10px] text-amber-800">
                  DEMO MODE
                </div>
              )}
            </div>
            <button
              className="mt-6 w-full rounded-lg py-2 text-sm font-medium text-white"
              style={{ background: config.primaryColor }}
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FieldProgramConfigPage;
